<template>
  <div>
    <template v-if="me !== undefined && me !== null">
      <nav-candidate-profile :name="name" />
      <v-container>
        <ui-max-width width="md" align="center">
          <v-alert :value="$t('study-profil-only-other-language-info') !== ''" type="info" :class="['mt-0', margin]">{{
            $t('study-profil-only-other-language-info')
          }}</v-alert>
          <template v-for="translation in me.profile.translations">
            <profile-completion
              :key="translation.language"
              v-show="translation.language === selectedLanguage && showProfileCompletion"
              :profile="me.profile"
              :translation="translation"
              :type="$consts('PROFIL_STUDENT')"
              :forcedLanguage="labelLanguage"
            />
          </template>
          <v-form ref="form" lazy-validation>
            <ui-stepper v-model="currentStepValue" @input="onCurrentStepChange" vertical non-linear>
              <ui-stepper-step editable complete step="1">{{
                $t('edit-candidate-student-profile-step1-label', labelLanguage)
              }}</ui-stepper-step>
              <ui-stepper-content step="1" :rules="step1Rules()">
                <v-card class="mb-3">
                  <v-card-text>
                    <form-month-picker
                      validate-on-blur
                      v-model="birthdate"
                      :rules="rules.birthdate"
                      :label="$t('edit-candidate-student-profile-birthdate-label', labelLanguage)"
                    />
                    <hint :label="$t('candidate-profile-birthdate-hint', labelLanguage)" />
                    <v-radio-group
                      v-model="willComeToCanadaWithFamilyMember"
                      :label="$t('candidate-profile-comes-to-canada-with-family-member-label', labelLanguage)"
                    >
                      <v-radio
                        v-for="option in yesNoOptionValues"
                        :key="`radio-${$slugiffy(option.label)}`"
                        :label="option.label"
                        :value="option.value"
                      ></v-radio>
                    </v-radio-group>
                    <v-text-field
                      v-if="showWillComeToCanadaWithFamilyMemberSpecification"
                      :label="$t('candidate-profile-comes-to-canada-with-family-member-specification-label', labelLanguage)"
                      v-model="willComeToCanadaWithFamilyMemberSpecification"
                    />
                  </v-card-text>
                </v-card>
                <v-card>
                  <v-card-text>
                    <v-textarea
                      :label="$t('candidate-profile-personal-description-label')"
                      v-model="personalDescriptionStudent"
                      :hint="$t('candidate-profile-personal-description-placeholder')"
                      persistent-hint
                      auto-grow
                      :counter="$consts('DESCRIPTION_MAX_LENGTH')"
                      :rules="descriptionRules()"
                    />
                  </v-card-text>
                </v-card>
              </ui-stepper-content>
              <ui-stepper-step editable complete step="2">
                {{ $t('edit-candidate-student-profile-step2-label', labelLanguage) }}
              </ui-stepper-step>
              <ui-stepper-content step="2">
                <v-card>
                  <v-card-text>
                    <form-education-level-select
                      :label="$t('candidate-profile-education-level-label', labelLanguage)"
                      :placeholder="$t('candidate-profile-education-level-placeholder', labelLanguage)"
                      v-model="educationLevel"
                    />
                    <hint :label="$t('candidate-profile-education-level-hint-label', labelLanguage)" for-combobox />

                    <v-radio-group v-model="trainingLevel" :label="$t('candidate-profile-training-level-label', labelLanguage)">
                      <v-radio
                        v-for="option in trainingLevelValues"
                        :key="`radio-${$slugiffy(option.label)}`"
                        :label="option.label"
                        :value="option.value"
                      ></v-radio>
                    </v-radio-group>

                    <v-radio-group
                      v-model="hasReportsAndDiplomasCopy"
                      :label="$t('candidate-profile-report-and-diploma-copy', labelLanguage)"
                    >
                      <v-radio
                        v-for="option in yesNoOptionValues"
                        :key="`radio-${$slugiffy(option.label)}`"
                        :label="option.label"
                        :value="option.value"
                      ></v-radio>
                    </v-radio-group>
                    <hint :label="$t('candidate-profile-report-and-diploma-copy-hint', labelLanguage)" />

                    <v-text-field
                      class="custom-text-input"
                      :label="$t('candidate-profile-overall-average-label', labelLanguage)"
                      v-model="overallAverage"
                    />
                    <hint :label="$t('candidate-profile-overall-average-hint-label', labelLanguage)" />

                    <form-study-rating-system
                      :label="$t('candidate-profile-rating-system-label', labelLanguage)"
                      v-model="ratingSystem"
                    />

                    <v-text-field
                      class="custom-text-input"
                      :label="$t('candidate-profile-last-year-average-label', labelLanguage)"
                      :rules="rules.lastYearAverage"
                      v-model="lastYearAverage"
                    />

                    <v-radio-group
                      v-model="currentlySubscribedToStudyProgram"
                      :label="$t('candidate-profile-currently-subscribed-to-study-program-label', labelLanguage)"
                    >
                      <v-radio
                        v-for="option in yesNoOptionValues"
                        :key="`radio-${$slugiffy(option.label)}`"
                        :label="option.label"
                        :value="option.value"
                      ></v-radio>
                    </v-radio-group>
                    <v-radio-group
                      v-if="currentlySubscribedToStudyProgram"
                      v-model="currentStudyProgramLevel"
                      :label="$t('candidate-profile-current-study-program-level-label', labelLanguage)"
                    >
                      <v-radio
                        v-for="option in trainingLevelValues"
                        :key="`radio-${$slugiffy(option.label)}`"
                        :label="option.label"
                        :value="option.value"
                      ></v-radio>
                    </v-radio-group>
                    <template v-if="isNotFromFrance">
                      <v-radio-group
                        v-model="frenchLanguageTestPassed"
                        :label="$t('candidate-profile-french-language-test-passed-label', labelLanguage)"
                      >
                        <v-radio
                          v-for="option in frenchLanguageTestPassedValues"
                          :key="`radio-${$slugiffy(option.label)}`"
                          :label="option.label"
                          :value="option.value"
                        ></v-radio>
                      </v-radio-group>
                      <hint :label="$t('candidate-profile-french-language-test-hint-label', labelLanguage)" />
                    </template>

                    <div>
                      <v-label>{{ $t('candidate-profile-training-related-label', labelLanguage) }}</v-label>
                      <v-checkbox
                        v-for="related in trainingRelatedValues"
                        :key="related.value"
                        v-model="trainingRelated"
                        :label="related.label"
                        :value="related.value"
                        class="my-3 py-0"
                        hide-details
                        @change="updateTrainingRelated(related.value)"
                      />
                    </div>
                  </v-card-text>
                </v-card>
              </ui-stepper-content>
              <template v-if="isNotFromFrance">
                <ui-stepper-step editable complete step="3">{{
                  $t('edit-candidate-student-profile-step3-label', labelLanguage)
                }}</ui-stepper-step>
                <ui-stepper-content step="3">
                  <v-card>
                    <v-card-text>
                      <v-radio-group
                        v-if="!isFrenchOrBelgium"
                        v-model="tuitionFeesAbleToPay"
                        :label="$t('candidate-profile-tuition-fees-able-pay', labelLanguage)"
                      >
                        <v-radio
                          v-for="option in getAmountWillingToPay()"
                          :key="`radio-${$slugiffy(option.label)}`"
                          :label="option.label"
                          :value="option.value"
                        ></v-radio>
                      </v-radio-group>
                      <hint
                        :label="$t('candidate-profile-has-first-study-year-financial-resources-hint-2-label', labelLanguage)"
                      />
                      <form-living-expenses-payer-select
                        v-if="showStudyExepensesPayerRadioButtons"
                        :label="$t('candidate-profile-study-expenses-payer-label', labelLanguage)"
                        v-model="studyExpensesPayer"
                      />
                      <v-text-field
                        v-if="showStudyExpensesPayerOtherSpecification"
                        :label="$t('candidate-profile-study-expenses-payer-other-specification-label', labelLanguage)"
                        v-model="studyExpensesPayerOtherSpecification"
                      />
                      <v-radio-group
                        v-model="hasFirstStudyYearLivingExpensesFinancialResources"
                        :label="
                          $t('candidate-profile-has-first-study-year-living-expenses-financial-resources-label', labelLanguage)
                        "
                      >
                        <v-radio
                          v-for="option in yesNoOptionValues"
                          :key="`radio-${$slugiffy(option.label)}`"
                          :label="option.label"
                          :value="option.value"
                        ></v-radio>
                      </v-radio-group>
                      <hint
                        :label="$t('candidate-profile-has-first-study-year-financial-resources-hint-2-label', labelLanguage)"
                      />
                      <form-living-expenses-payer-select
                        v-if="hasFirstStudyYearLivingExpensesFinancialResources"
                        :label="$t('candidate-profile-living-expenses-payer-label', labelLanguage)"
                        v-model="livingExpensesPayer"
                      />
                      <v-text-field
                        v-if="showLivingExpensesPayerOtherSpecification"
                        :label="$t('candidate-profile-living-expenses-payer-other-specification-label', labelLanguage)"
                        v-model="livingExpensesPayerOtherSpecification"
                      />
                      <div>
                        <v-label>{{ $t('candidate-profile-financial-resources-proofs-label', labelLanguage) }}</v-label>
                        <v-checkbox
                          v-for="proof in financialResourcesProofsValues"
                          :key="proof.value"
                          v-model="financialResourcesProofs"
                          :label="proof.label"
                          :value="proof.value"
                          class="my-3 py-0"
                          hide-details
                          @change="updateFinancialResourcesProofs(proof.value)"
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                </ui-stepper-content>
                <ui-stepper-step editable complete step="4">{{
                  $t('edit-candidate-student-profile-step4-label', labelLanguage)
                }}</ui-stepper-step>
                <ui-stepper-content step="4">
                  <v-card>
                    <v-card-text>
                      <v-radio-group
                        v-model="isInterestedInPartTimeJob"
                        :label="$t('candidate-profile-is-interested-in-part-time-job-label', labelLanguage)"
                      >
                        <v-radio
                          v-for="option in yesNoOptionValues"
                          :key="`radio-${$slugiffy(option.label)}`"
                          :label="option.label"
                          :value="option.value"
                        ></v-radio>
                      </v-radio-group>
                      <hint :label="$t('candidate-profile-is-interested-in-part-time-job-hint-label', labelLanguage)" />
                      <v-radio-group
                        v-model="isInterestedInJobAfterGraduating"
                        :label="$t('candidate-profile-is-interested-in-job-after-graduating-label', labelLanguage)"
                      >
                        <v-radio
                          v-for="option in yesNoOptionValues"
                          :key="`radio-${$slugiffy(option.label)}`"
                          :label="option.label"
                          :value="option.value"
                        ></v-radio>
                      </v-radio-group>
                      <v-text-field
                        :label="$t('candidate-profile-dream-job-description-label', labelLanguage)"
                        v-model="dreamJobDescription"
                      />
                      <v-radio-group
                        v-model="homeCountryJobProspects"
                        :label="$t('candidate-profile-home-country-job-prospects-label', labelLanguage)"
                      >
                        <v-radio
                          v-for="option in homeCountryJobProspectsValues"
                          :key="`radio-${$slugiffy(option.label)}`"
                          :label="option.label"
                          :value="option.value"
                        ></v-radio>
                      </v-radio-group>
                    </v-card-text>
                  </v-card>
                </ui-stepper-content>
              </template>
            </ui-stepper>
          </v-form>
        </ui-max-width>
        <ui-action>
          <v-btn color="primary" round large @click="onPublishProfilClick">{{ $t('edit-candidate-submit-btn') }}</v-btn>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn color="primary" icon outline large round @click="onPreviewProfilClick" v-on="on">
                <v-icon>visibility</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('candidate-profiles-preview') }}</span>
          </v-tooltip>
        </ui-action>
      </v-container>
    </template>
  </div>
</template>

<script>
import Hint from '@/components/form/hint'
import NavCandidateProfile from '@/components/nav/nav-candidate-profile'
import FormMonthPicker from '@/components/form/month-picker'
import ProfileCompletion from '@/components/candidate/profile-completion'
import FormEducationLevelSelect from '@/components/form/education-level-select'
import FormLivingExpensesPayerSelect from '@/components/form/living-expenses-payer-select'
import FormStudyExpensesPayerSelect from '@/components/form/study-expenses-payer-select'
import FormStudyRatingSystem from '@/components/form/study-rating-system-select'

import Notification from '@/services/notification'
import Candidate from '@/services/candidate'
import ProfileCompletionHelpers from '@/helpers/profile-completion'

import FormHelper from '@/helpers/form'
import i18n from '@/helpers/i18n'
import { Consts } from '@/helpers/consts'
import { Form } from '@/mixins/form'
import { TuitionFeesAbleToPayMixin } from '@/mixins/tuition-fees-able-to-pay'

import gql from 'graphql-tag'

const GET_MY_CANDIDATE_PROFILE_EDITION = gql`
  query MyProfileEdition($countryLanguage: CountryLanguage) {
    me {
      id
      email
      type
      profile {
        id
        ... on CandidateProfileGraphType {
          city
          domains
          experienceLevel
          firstName
          hasCsq
          hasPassport
          hasTraveled
          hasTraveledToCanada
          hasCanFam
          hasVisaRefused
          hasInfraction
          hasEntryRefused
          visaRefusedType
          country(language: $countryLanguage) {
            id
            countryCode
            name
          }
          nationalities(language: $countryLanguage) {
            id
            countryCode
            name
          }
          languages {
            id
            language
            level
          }
          lastName
          linkedInProfile
          phoneNumber
          cellphoneNumber
          picture
          birthdate
          educationLevel
          overallAverage
          ratingSystem
          lastYearAverage
          currentlySubscribedToStudyProgram
          hasReportsAndDiplomasCopy
          currentStudyProgramLevel
          frenchLanguageTestPassed
          trainingLevel
          hasFirstStudyYearLivingExpensesFinancialResources
          tuitionFeesAbleToPay
          livingExpensesPayer
          studyExpensesPayer
          financialResourcesProofs
          trainingRelated
          willComeToCanadaWithFamilyMember
          isInterestedInPartTimeJob
          isInterestedInJobAfterGraduating
          homeCountryJobProspects
          translations {
            id
            competencies
            education {
              id
              details
              domain
              graduationDate
              level
              schoolName
              startDate
            }
            experience {
              id
              companyName
              endDate
              jobTitle
              location
              responsibilities
              startDate
            }
            immigrationProject
            language
            links {
              id
              description
              link
              title
            }
            personalDescriptionStudent
            visaRefusedReason
            livingExpensesPayerOtherSpecification
            studyExpensesPayerOtherSpecification
            willComeToCanadaWithFamilyMemberSpecification
            dreamJobDescription
          }
        }
      }
    }
  }
`

export default {
  components: {
    Hint,
    NavCandidateProfile,
    FormMonthPicker,
    ProfileCompletion,
    FormEducationLevelSelect,
    FormLivingExpensesPayerSelect,
    FormStudyExpensesPayerSelect,
    FormStudyRatingSystem
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (from.path !== '/') {
        vm.prevRoute = from.fullPath
      }
    })
  },
  mixins: [Form, TuitionFeesAbleToPayMixin],
  data() {
    return {
      rules: {
        birthdate: [value => FormHelper.isValidDate(value) || i18n.instance().t('error-date-not-valid')],
        lastYearAverage: [
          value => {
            if (this.ratingSystem === '') {
              return true
            }
            if (this.ratingSystem == Consts.RATING_SYSTEM_STUDY_OTHER) {
              return !FormHelper.stringEmpty(value) || i18n.instance().t('error-last-year-average-other-not-valid')
            }
            const maxAverage =
              this.ratingSystem === Consts.RATING_SYSTEM_STUDY_FIVE
                ? 5
                : this.ratingSystem === Consts.RATING_SYSTEM_STUDY_TEN
                ? 10
                : this.ratingSystem === Consts.RATING_SYSTEM_STUDY_TWENTY
                ? 20
                : this.ratingSystem === Consts.RATING_SYSTEM_STUDY_HUNDRED
                ? 100
                : Number.MAX_VALUE
            return (
              FormHelper.isNumberBetween(value, 0, maxAverage) ||
              i18n.instance().t('error-last-year-average-not-valid', { maxAverage })
            )
          }
        ]
      },
      tuitionFeesAbleToPay: null,
      isFrenchOrBelgium: false,
      prevRoute: '',
      selectedLanguage: Consts.CONTENT_LANGUAGE_FR, // French is always selected for now
      labelLanguage: 'fr', // French is always selected for now
      birthdate: null,
      educationLevel: '',
      overallAverage: '',
      ratingSystem: '',
      lastYearAverage: '',
      currentlySubscribedToStudyProgram: null,
      hasReportsAndDiplomasCopy: null,
      currentStudyProgramLevel: '',
      frenchLanguageTestPassed: '',
      trainingLevel: '',
      hasFirstStudyYearLivingExpensesFinancialResources: null,
      livingExpensesPayer: '',
      studyExpensesPayer: '',
      livingExpensesPayerOtherSpecification: '',
      studyExpensesPayerOtherSpecification: '',
      financialResourcesProofs: [],
      trainingRelated: [],
      nationalities: [],
      willComeToCanadaWithFamilyMember: null,
      willComeToCanadaWithFamilyMemberSpecification: '',
      isInterestedInPartTimeJob: '',
      isInterestedInJobAfterGraduating: '',
      dreamJobDescription: '',
      personalDescriptionStudent: '',
      homeCountryJobProspects: '',
      homeCountryJobProspectsValues: [
        {
          value: Consts.HOME_COUNTRY_JOB_PROSPECTS_BAD,
          label: this.$t('home-country-job-prospect-bad', 'fr')
        },
        {
          value: Consts.HOME_COUNTRY_JOB_PROSPECTS_GOOD,
          label: this.$t('home-country-job-prospect-good', 'fr')
        },
        {
          value: Consts.HOME_COUNTRY_JOB_PROSPECTS_VERY_GOOD,
          label: this.$t('home-country-job-prospect-very-good', 'fr')
        },
        {
          value: Consts.HOME_COUNTRY_JOB_PROSPECTS_UNKNOWN,
          label: this.$t('home-country-job-prospect-unknown', 'fr')
        }
      ],
      financialResourcesProofsValues: [
        {
          value: Consts.FINANCIAL_RESOURCE_PROOF_BANK_TRANSACTION,
          label: this.$t('financial-resource-proof-bank-transaction', 'fr')
        },
        {
          value: Consts.FINANCIAL_RESOURCE_PROOF_COUNTRY_CONTROL_OFFICE_FUNDS,
          label: this.$t('financial-resource-proof-country-control-office-funds', 'fr')
        },
        {
          value: Consts.FINANCIAL_RESOURCE_PROOF_SALARY_STATEMENT,
          label: this.$t('financial-resource-proof-salary-statement', 'fr')
        },
        {
          value: Consts.FINANCIAL_RESOURCE_PROOF_TAX_STATEMENT,
          label: this.$t('financial-resource-proof-tax-statement', 'fr')
        },
        {
          value: Consts.FINANCIAL_RESOURCE_PROOF_BANK_FUNDS,
          label: this.$t('financial-resource-proof-bank-funds', 'fr')
        },
        {
          value: Consts.FINANCIAL_RESOURCE_PROOF_BANK_LETTER,
          label: this.$t('financial-resource-proof-bank-letter', 'fr')
        },
        {
          value: Consts.FINANCIAL_RESOURCE_PROOF_MORTGAGE,
          label: this.$t('financial-resource-proof-mortage', 'fr')
        },
        {
          value: Consts.FINANCIAL_RESOURCE_PROOF_NONE,
          label: this.$t('financial-resource-proof-none', 'fr')
        }
      ],
      trainingRelatedValues: [
        {
          value: Consts.TRAINING_RELATED_MATHEMATICS,
          label: this.$t('training-related-mathematics', 'fr')
        },
        {
          value: Consts.TRAINING_RELATED_SCIENCE,
          label: this.$t('training-related-science', 'fr')
        },
        {
          value: Consts.TRAINING_RELATED_PHYSICAL,
          label: this.$t('training-related-physical', 'fr')
        },
        {
          value: Consts.TRAINING_RELATED_CHEMISTRY,
          label: this.$t('training-related-chemistry', 'fr')
        },
        {
          value: Consts.TRAINING_RELATED_NONE,
          label: this.$t('training-related-none', 'fr')
        }
      ],
      frenchLanguageTestPassedValues: [
        {
          value: Consts.FRENCH_LANGUAGE_TEST_PASSED_YES,
          label: this.$t('yes-option', 'fr')
        },
        {
          value: Consts.FRENCH_LANGUAGE_TEST_PASSED_NO,
          label: this.$t('no-option', 'fr')
        },
        {
          value: Consts.FRENCH_LANGUAGE_TEST_PASSED_NO_BUT_FRENCH_IS_FIRST_LANGUAGE,
          label: this.$t('candidate-profile-french-language-test-passed-no-but-french-is-first-language-label', 'fr')
        }
      ],
      trainingLevelValues: [
        {
          value: Consts.TRAINING_LEVEL_PRE_UNIVERSITY,
          label: this.$t('candidate-profile-training-level-pre-university', 'fr')
        },
        {
          value: Consts.TRAINING_LEVEL_BACHELOR,
          label: this.$t('candidate-profile-training-level-bachelor', 'fr')
        },
        {
          value: Consts.TRAINING_LEVEL_PROFESSIONAL_MASTERS,
          label: this.$t('candidate-profile-training-level-master-pro', 'fr')
        },
        {
          value: Consts.TRAINING_LEVEL_DOCTORATE,
          label: this.$t('candidate-profile-training-level-doctorate', 'fr')
        },
        {
          value: Consts.TRAINING_LEVEL_NONE,
          label: this.$t('candidate-profile-training-level-none', 'fr')
        }
      ],
      yesNoOptionValues: [
        {
          value: true,
          label: this.$t('yes-option', 'fr')
        },
        {
          value: false,
          label: this.$t('no-option', 'fr')
        }
      ]
    }
  },
  apollo: {
    me: {
      query: GET_MY_CANDIDATE_PROFILE_EDITION,
      variables() {
        return {
          countryLanguage: this.$currentCountryLanguage()
        }
      },
      result({ data, loading }) {
        if (data === undefined) {
          if (!loading) {
            //throw error
          }
          return
        }
        const profile = data.me.profile
        const translation = data.me.profile.translations.first(x => x.language === Consts.CONTENT_LANGUAGE_FR)
        this.birthdate = profile.birthdate ? profile.birthdate.split('T')[0] : null // Doing dayjs(profile.birthdate).format('YYYY-MM-DD') does a weird glitch in the UI
        this.educationLevel = profile.educationLevel ? profile.educationLevel : ''
        this.overallAverage = profile.overallAverage ? profile.overallAverage : ''
        this.ratingSystem = profile.ratingSystem ? profile.ratingSystem : ''
        this.lastYearAverage = profile.lastYearAverage ? profile.lastYearAverage : null
        this.currentlySubscribedToStudyProgram =
          profile.currentlySubscribedToStudyProgram !== null ? profile.currentlySubscribedToStudyProgram : null
        this.hasReportsAndDiplomasCopy = profile.hasReportsAndDiplomasCopy !== null ? profile.hasReportsAndDiplomasCopy : null
        this.currentStudyProgramLevel = profile.currentStudyProgramLevel ? profile.currentStudyProgramLevel : ''
        this.frenchLanguageTestPassed = profile.frenchLanguageTestPassed ? profile.frenchLanguageTestPassed : ''
        this.trainingLevel = profile.trainingLevel ? profile.trainingLevel : ''

        this.nationalities = data.me.profile.nationalities.map(nationality => nationality.countryCode)

        this.tuitionFeesAbleToPay = profile.tuitionFeesAbleToPay !== null ? profile.tuitionFeesAbleToPay : null
        this.isFrenchOrBelgium =
          profile.country &&
          (profile.country.countryCode === Consts.FRANCE_COUNTRY_CODE ||
            profile.country.countryCode === Consts.BELGIUM_COUNTRY_CODE)

        this.hasFirstStudyYearLivingExpensesFinancialResources =
          profile.hasFirstStudyYearLivingExpensesFinancialResources !== null
            ? profile.hasFirstStudyYearLivingExpensesFinancialResources
            : null
        this.livingExpensesPayer = profile.livingExpensesPayer ? profile.livingExpensesPayer : ''
        this.studyExpensesPayer = profile.studyExpensesPayer ? profile.studyExpensesPayer : ''
        this.financialResourcesProofs = profile.financialResourcesProofs
        this.trainingRelated = profile.trainingRelated
        this.willComeToCanadaWithFamilyMember =
          profile.willComeToCanadaWithFamilyMember !== null ? profile.willComeToCanadaWithFamilyMember : null
        this.isInterestedInPartTimeJob = profile.isInterestedInPartTimeJob !== null ? profile.isInterestedInPartTimeJob : null
        this.isInterestedInJobAfterGraduating =
          profile.isInterestedInJobAfterGraduating !== null ? profile.isInterestedInJobAfterGraduating : null
        this.homeCountryJobProspects = profile.homeCountryJobProspects ? profile.homeCountryJobProspects : ''

        if (translation) {
          this.livingExpensesPayerOtherSpecification = translation.livingExpensesPayerOtherSpecification
            ? translation.livingExpensesPayerOtherSpecification
            : ''
          this.studyExpensesPayerOtherSpecification = translation.studyExpensesPayerOtherSpecification
            ? translation.studyExpensesPayerOtherSpecification
            : ''
          this.willComeToCanadaWithFamilyMemberSpecification = translation.willComeToCanadaWithFamilyMemberSpecification
            ? translation.willComeToCanadaWithFamilyMemberSpecification
            : ''
          this.dreamJobDescription = translation.dreamJobDescription ? translation.dreamJobDescription : ''
          this.personalDescriptionStudent = translation.personalDescriptionStudent ? translation.personalDescriptionStudent : ''
        }
      }
    }
  },
  computed: {
    name: function() {
      return `${this.me.profile.firstName} ${this.me.profile.lastName}`
    },
    margin() {
      return this.mobile ? 'mb-3' : 'mb-4'
    },
    userId: function() {
      return this.me.id
    },
    showProfileCompletion: function() {
      const selectedTranslation = this.me.profile.translations.find(x => x.language === this.selectedLanguage)
      if (selectedTranslation !== null && selectedTranslation !== undefined) {
        const result = ProfileCompletionHelpers.getProfileStudentCompletion(this.me.profile, selectedTranslation)
        return !result.candidateProfileCompleted
      } else {
        return false
      }
    },
    showLivingExpensesPayerOtherSpecification: function() {
      return (
        this.hasFirstStudyYearLivingExpensesFinancialResources && this.livingExpensesPayer === Consts.LIVING_EXPENSES_PAYER_OTHER
      )
    },
    showStudyExpensesPayerOtherSpecification: function() {
      return this.studyExpensesPayer === Consts.STUDY_EXPENSES_PAYER_OTHER
    },
    showWillComeToCanadaWithFamilyMemberSpecification: function() {
      return this.willComeToCanadaWithFamilyMember
    },
    showStudyExepensesPayerRadioButtons: function() {
      return this.tuitionFeesAbleToPay && this.tuitionFeesAbleToPay !== Consts.AMOUNT_WILLING__TO_PAY_NOT_ABLE_TO_PAY
    },
    isNotFromFrance: function() {
      return this.nationalities.indexOf('fr') === -1
    }
  },
  methods: {
    step1Rules: function() {
      return [
        value =>
          !this.showValidationError ||
          (this.personalDescriptionStudent === null || this.personalDescriptionStudent.length <= Consts.DESCRIPTION_MAX_LENGTH)
      ]
    },
    onCurrentStepChange: async function(val) {
      if (!this.$apollo.loading) {
        this.publishProfil()
      }
    },
    onPublishProfilClick: async function() {
      if (this.$refs.form.validate()) {
        await this.publishProfil()
        let route = i18n.getLocalizedUrl(Consts.URL_CANDIDATE_CV)
        if (this.prevRoute !== '') {
          route = this.prevRoute
        }
        this.$router.replace(route)
      }
    },
    onPreviewProfilClick: async function() {
      if (this.$refs.form.validate()) {
        await this.publishProfil()
        this.$router.push(i18n.getLocalizedUrl(Consts.URL_CANDIDATE_PROFIL_EDUCATIONAL_PREVIEW))
      }
    },
    publishProfil: async function() {
      if (this.$refs.form !== undefined && this.$refs.form.validate() && this.selectedLanguage !== null) {
        let input = { userId: this.userId }
        let inputLang = {
          userId: this.userId,
          language: this.selectedLanguage
        }

        if (this.birthdate !== null) {
          input.birthdate = { value: this.birthdate }
        }

        if (this.educationLevel !== null && this.educationLevel.trim() !== '') {
          input.educationLevel = { value: this.educationLevel }
        }

        if (this.personalDescriptionStudent !== null && this.personalDescriptionStudent.length <= Consts.DESCRIPTION_MAX_LENGTH) {
          inputLang.personalDescriptionStudent = { value: this.personalDescriptionStudent }
        }

        if (this.overallAverage !== null) {
          input.overallAverage = { value: this.overallAverage }
        }

        if (this.ratingSystem !== '') {
          input.ratingSystem = { value: this.ratingSystem }
        }

        if (this.lastYearAverage !== null) {
          if (this.lastYearAverage === '') {
            input.lastYearAverage = { value: null }
          } else {
            input.lastYearAverage = { value: this.lastYearAverage }
          }
        }

        if (this.currentlySubscribedToStudyProgram !== null) {
          input.currentlySubscribedToStudyProgram = { value: this.currentlySubscribedToStudyProgram }
        }

        if (this.hasReportsAndDiplomasCopy !== null) {
          input.hasReportsAndDiplomasCopy = { value: this.hasReportsAndDiplomasCopy }
        }

        if (this.currentlySubscribedToStudyProgram && this.currentStudyProgramLevel.trim() !== '') {
          input.currentStudyProgramLevel = { value: this.currentStudyProgramLevel }
        } else {
          input.currentStudyProgramLevel = { value: null }
        }

        if (this.frenchLanguageTestPassed !== null && this.frenchLanguageTestPassed.trim() !== '') {
          input.frenchLanguageTestPassed = { value: this.frenchLanguageTestPassed }
        }

        if (this.trainingLevel !== null && this.trainingLevel.trim() !== '') {
          input.trainingLevel = { value: this.trainingLevel }
        }

        if (this.tuitionFeesAbleToPay !== null) {
          input.tuitionFeesAbleToPay = { value: this.tuitionFeesAbleToPay }
        }

        if (this.tuitionFeesAbleToPay !== null && this.tuitionFeesAbleToPay == Consts.AMOUNT_WILLING__TO_PAY_NOT_ABLE_TO_PAY) {
          input.studyExpensesPayer = { value: null }
          inputLang.studyExpensesPayerOtherSpecification = { value: null }
        } else {
          if (this.studyExpensesPayer !== null && this.studyExpensesPayer.trim() !== '') {
            input.studyExpensesPayer = { value: this.studyExpensesPayer }
          }
          if (this.studyExpensesPayerOtherSpecification !== null) {
            inputLang.studyExpensesPayerOtherSpecification = { value: this.studyExpensesPayerOtherSpecification }
          }
        }

        if (this.hasFirstStudyYearLivingExpensesFinancialResources !== null) {
          input.hasFirstStudyYearLivingExpensesFinancialResources = {
            value: this.hasFirstStudyYearLivingExpensesFinancialResources
          }
        }

        if (this.hasFirstStudyYearLivingExpensesFinancialResources === false) {
          input.livingExpensesPayer = { value: null }
          inputLang.livingExpensesPayerOtherSpecification = { value: null }
        } else {
          if (this.livingExpensesPayer !== null && this.livingExpensesPayer.trim() !== '') {
            input.livingExpensesPayer = { value: this.livingExpensesPayer }
          }
          if (this.livingExpensesPayerOtherSpecification !== null) {
            inputLang.livingExpensesPayerOtherSpecification = { value: this.livingExpensesPayerOtherSpecification }
          }
        }

        if (this.financialResourcesProofs !== null) {
          input.financialResourcesProofs = { value: this.financialResourcesProofs }
        }

        if (this.trainingRelated !== null) {
          input.trainingRelated = { value: this.trainingRelated }
        }

        if (this.willComeToCanadaWithFamilyMember !== null) {
          input.willComeToCanadaWithFamilyMember = { value: this.willComeToCanadaWithFamilyMember }
        }

        if (this.isInterestedInPartTimeJob !== null) {
          input.isInterestedInPartTimeJob = { value: this.isInterestedInPartTimeJob }
        }

        if (this.isInterestedInJobAfterGraduating !== null) {
          input.isInterestedInJobAfterGraduating = { value: this.isInterestedInJobAfterGraduating }
        }

        if (this.homeCountryJobProspects !== null && this.homeCountryJobProspects.trim() !== '') {
          input.homeCountryJobProspects = { value: this.homeCountryJobProspects }
        }

        if (this.willComeToCanadaWithFamilyMemberSpecification !== null) {
          inputLang.willComeToCanadaWithFamilyMemberSpecification = { value: this.willComeToCanadaWithFamilyMemberSpecification }
        }

        if (this.dreamJobDescription !== null) {
          inputLang.dreamJobDescription = { value: this.dreamJobDescription }
        }

        await Candidate.updateCandidateProfile(input, this.$currentCountryLanguage())
        await Candidate.updateCandidateProfileTranslation(inputLang, this.$currentCountryLanguage())
        Notification.showInfo(i18n.instance().t('save-candidate-success'), 6000)
      } else {
        this.showValidationError = true
      }
    },
    createProfile: async function() {
      let inputLang = {
        userId: this.me.id,
        language: this.selectedLanguage
      }

      await Candidate.updateCandidateProfileTranslation(inputLang, this.$currentCountryLanguage())
    },
    updateFinancialResourcesProofs: function(value) {
      if (value === Consts.FINANCIAL_RESOURCE_PROOF_NONE) {
        if (this.financialResourcesProofs.filter(x => x === Consts.FINANCIAL_RESOURCE_PROOF_NONE).length > 0) {
          this.financialResourcesProofs = [Consts.FINANCIAL_RESOURCE_PROOF_NONE]
        }
      } else {
        this.financialResourcesProofs = this.financialResourcesProofs.filter(x => x !== Consts.FINANCIAL_RESOURCE_PROOF_NONE)
      }
    },
    updateTrainingRelated: function(value) {
      if (value === Consts.TRAINING_RELATED_NONE) {
        if (this.trainingRelated.filter(x => x === Consts.TRAINING_RELATED_NONE).length > 0) {
          this.trainingRelated = [Consts.TRAINING_RELATED_NONE]
        }
      } else {
        this.trainingRelated = this.trainingRelated.filter(x => x !== Consts.TRAINING_RELATED_NONE)
      }
    },
    descriptionRules: function() {
      return [
        value =>
          FormHelper.stringEmpty(value) ||
          value.length <= Consts.DESCRIPTION_MAX_LENGTH ||
          i18n.instance().t('error-create-candidate-profil-description-length')
      ]
    }
  }
}
</script>
